import React, { useState } from 'react';
import { NameEmailAvatar } from '../../../../../../ui/components/Common/NameEmailAvatar';
import {
  CCAApplicantResult,
  CCAQuestionResult,
  CCAResultSummary,
  CCAStatus,
} from '../../../../../../utils/types/cca_types';
import { useGetStorageUrlQuery } from '../../../../../../utils/api_service/ccaApi';
import { EnrichedResultSummary, computeSubdomainScores, useCCAResults } from './hooks/useCCAResults';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDisplayName = (s: string) => {
  if (s.includes('_')) {
    return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  // camelCase → words
  return s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};

const formatDate = (val?: number | string | null) => {
  if (val == null) return '—';
  try {
    const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

const activeTimeColor = (seconds: number | null | undefined): string => {
  if (seconds == null) return 'text-slate-400';
  if (seconds < 30) return 'text-rose-500';
  if (seconds < 120) return 'text-amber-500';
  return 'text-slate-600';
};

const scoreColor = (pct: number): string => {
  if (pct >= 70) return 'text-emerald-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-rose-600';
};

// ─── Score display ────────────────────────────────────────────────────────────

interface ScoreDisplay {
  text: string;
  pct: number | null;
}

const getScoreDisplay = (summary: CCAResultSummary): ScoreDisplay | null => {
  if (summary.totalPct != null) {
    return { text: `${Math.round(summary.totalPct)}%`, pct: summary.totalPct };
  }
  if (summary.totalScore != null) {
    if (summary.maxScore != null) {
      const max = summary.maxScore;
      return {
        text: `${summary.totalScore}/${max} pts`,
        pct: max > 0 ? (summary.totalScore / max) * 100 : 0,
      };
    }
    return { text: `${summary.totalScore} pts`, pct: null };
  }
  return null;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<CCAStatus, { cls: string; dot: string }> = {
  Uninvited: { cls: 'bg-slate-100 text-slate-600',    dot: 'bg-slate-400' },
  'Invite Sent': { cls: 'bg-blue-50 text-blue-700',       dot: 'bg-blue-500' },
  'In Progress': { cls: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500' },
  Completed: { cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  Expired: { cls: 'bg-rose-50 text-rose-600',       dot: 'bg-rose-400' },
  Superseded: { cls: 'bg-slate-100 text-slate-500',    dot: 'bg-slate-300' },
};

const StatusBadge: React.FC<{ status: CCAStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG['Uninvited'];
  return (
    <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      { status }
    </span>
  );
};

// ─── Subdomain bar colours ────────────────────────────────────────────────────

const SUBDOMAIN_BAR: Record<string, string> = {
  abstract_reasoning: 'bg-violet-500',
  logical_reasoning: 'bg-blue-500',
  quantitative_aptitude: 'bg-emerald-500',
  verbal_ability: 'bg-amber-500',
  data_interpretation: 'bg-rose-500',
  'Abstract Reasoning': 'bg-violet-500',
  'Logical Reasoning': 'bg-blue-500',
  'Quantitative Aptitude': 'bg-emerald-500',
  'Verbal Ability': 'bg-amber-500',
  'Data Interpretation': 'bg-rose-500',
};

const barColor = (subdomain: string) =>
  SUBDOMAIN_BAR[subdomain] ?? 'bg-slate-400';

// ─── Storage image ────────────────────────────────────────────────────────────

const CCAStorageImage: React.FC<{
  imageKey: string;
  alt?: string;
  className?: string;
}> = ({ imageKey, alt = 'Image', className }) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey, {
    skip: !imageKey,
  });
  if (isLoading) {
    return (
      <div className='animate-pulse bg-slate-200 rounded-lg h-24 w-full' />
    );
  }
  if (isError || !data?.url) {
    return (
      <span className='text-xs text-slate-400 italic'>Image unavailable</span>
    );
  }
  return (
    <img
        src={data.url}
        alt={alt}
        className={className ?? 'max-h-40 max-w-full object-contain rounded-lg'}
    />
  );
};

// ─── Detail panel ─────────────────────────────────────────────────────────────

const SubdomainBreakdown: React.FC<{
  subdomainScores: Record<string, number>;
}> = ({ subdomainScores }) => {
  const entries = Object.entries(subdomainScores);
  if (entries.length === 0) return null;
  return (
    <div>
      <p className='text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2'>
        Subdomain Breakdown
      </p>
      <div className='space-y-2.5'>
        { entries.map(([sd, score]) => (
          <div key={sd}>
            <div className='flex items-center justify-between mb-1'>
              <span className='text-xs text-slate-600 font-medium'>{ toDisplayName(sd) }</span>
              <span className={`text-xs font-bold ${scoreColor(score)}`}>
                { Math.round(score) }%
              </span>
            </div>
            <div className='h-1.5 rounded-full bg-slate-200'>
              <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${barColor(sd)}`}
                  style={{ width: `${Math.min(score, 100)}%` }}
              />
            </div>
          </div>
        )) }
      </div>
    </div>
  );
};

const QuestionAccordion: React.FC<{
  questionResults: CCAQuestionResult[] | null | undefined;
}> = ({ questionResults }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const items = (questionResults ?? []).slice().sort((a, b) => a.orderKey - b.orderKey);
  if (items.length === 0) return null;
  return (
    <div>
      <p className='text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2'>
        Questions ({ items.length })
      </p>
      <div className='rounded-lg border border-slate-100 divide-y divide-slate-100 overflow-hidden'>
        { items.map((qr, i) => {
          const isOpen = expandedId === qr.id;
          const correct = qr.isCorrect;
          const snap = qr.questionSnapshot;
          const questionText = snap?.question?.text ?? '(no question text)';
          const questionImageKey = snap?.question?.imageKey ?? null;
          // Prefer the snapshot's questionType — qr.questionType may be stale or missing
          const isMCQ = (snap?.questionType ?? qr.questionType) === 'multiple_choice';
          const selectedOption = isMCQ
            ? snap?.options?.find((o) => o.id === qr.selectedOptionId)
            : undefined;
          const correctOption = isMCQ
            ? snap?.options?.find((o) => o.id === snap?.correctOptionId)
            : undefined;
          const level = snap?.level;
          const aiScorePct = qr.aiScore != null ? Math.round(qr.aiScore * 100) : null;

          const badgeCls =
            correct === true
              ? 'bg-emerald-100 text-emerald-700'
              : correct === false
                ? 'bg-rose-100 text-rose-700'
                : 'bg-amber-50 text-amber-600';

          const answerCls =
            correct === true
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
              : correct === false
                ? 'bg-rose-50 text-rose-800 border border-rose-100'
                : 'bg-amber-50 text-amber-800 border border-amber-100';

          // For short-answer: colour the border by AI score
          const shortAnswerCls =
            aiScorePct == null
              ? 'bg-slate-50 text-slate-700 border border-slate-200'
              : aiScorePct >= 70
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                : aiScorePct >= 40
                  ? 'bg-amber-50 text-amber-800 border border-amber-100'
                  : 'bg-rose-50 text-rose-800 border border-rose-100';

          return (
            <div key={qr.id} className='bg-white'>
              { /* ── Row header ─────────────────────────────── */ }
              <button
                  type='button'
                  className='w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-slate-50/70 transition-colors'
                  onClick={() => setExpandedId(isOpen ? null : qr.id)}
              >
                <span
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${badgeCls}`}
                >
                  { i + 1 }
                </span>
                <p className='flex-1 text-xs text-slate-700 line-clamp-2 leading-relaxed'>
                  { questionText }
                  { questionImageKey && (
                    <span className='ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-slate-400'>
                      <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                        <rect x='3' y='3' width='18' height='18' rx='2' />
                        <circle cx='8.5' cy='8.5' r='1.5' />
                        <path strokeLinecap='round' strokeLinejoin='round' d='M21 15l-5-5L5 21' />
                      </svg>
                      image
                    </span>
                  ) }
                </p>
                <div className='flex items-center gap-1.5 flex-shrink-0'>
                  { !isMCQ && aiScorePct != null ? (
                    <span className={`text-[11px] font-bold tabular-nums ${scoreColor(aiScorePct)}`}>
                      { aiScorePct }%
                    </span>
                  ) : isMCQ && correct !== null ? (
                    <span className={correct ? 'text-emerald-500' : 'text-rose-500'}>
                      <svg className='w-3.5 h-3.5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.5}>
                        { correct
                          ? <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                          : <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                        }
                      </svg>
                    </span>
                  ) : null }
                  <svg
                      className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
                  </svg>
                </div>
              </button>

              { /* ── Expanded detail ─────────────────────────── */ }
              { isOpen && (
                <div className='px-4 pt-3 pb-4 bg-slate-50/60 border-t border-slate-100 space-y-3'>

                  { /* Question image (if present) */ }
                  { questionImageKey && (
                    <div className='flex justify-center bg-white rounded-lg border border-slate-100 p-3'>
                      <CCAStorageImage
                          imageKey={questionImageKey}
                          alt='Question image'
                          className='max-h-48 max-w-full object-contain rounded'
                      />
                    </div>
                  ) }

                  { /* 3-column answer grid — stacks to 1-col on mobile */ }
                  <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>

                    { /* ① Candidate's Answer */ }
                    <div className='flex flex-col gap-1.5'>
                      <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-400'>
                        Candidate&apos;s Answer
                      </p>
                      { isMCQ ? (
                        selectedOption ? (
                          <div className={`flex-1 rounded-lg px-2.5 py-2.5 text-xs font-medium ${answerCls}`}>
                            { selectedOption.imageKey ? (
                              <CCAStorageImage
                                  imageKey={selectedOption.imageKey}
                                  alt={selectedOption.text ?? 'Selected option'}
                                  className='max-h-28 max-w-full object-contain rounded'
                              />
                            ) : (
                              selectedOption.text
                            ) }
                          </div>
                        ) : (
                          <p className='text-xs text-slate-400 italic px-1 pt-1'>No answer</p>
                        )
                      ) : (
                        qr.shortAnswerText ? (
                          <div className={`flex-1 rounded-lg px-2.5 py-2.5 text-xs font-medium whitespace-pre-wrap ${shortAnswerCls}`}>
                            { qr.shortAnswerText.trim() }
                          </div>
                        ) : (
                          <p className='text-xs text-slate-400 italic px-1 pt-1'>No answer</p>
                        )
                      ) }
                    </div>

                    { /* ② Correct Answer (MCQ) ─ or ─ AI Score (short-answer) */ }
                    <div className='flex flex-col gap-1.5'>
                      <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-400'>
                        { isMCQ ? 'Correct Answer' : 'AI Score' }
                      </p>
                      { isMCQ ? (
                        correctOption ? (
                          <div className='flex-1 rounded-lg px-2.5 py-2.5 text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-100'>
                            { correctOption.imageKey ? (
                              <CCAStorageImage
                                  imageKey={correctOption.imageKey}
                                  alt={correctOption.text ?? 'Correct option'}
                                  className='max-h-28 max-w-full object-contain rounded'
                              />
                            ) : (
                              correctOption.text
                            ) }
                          </div>
                        ) : (
                          <p className='text-xs text-slate-400 italic px-1 pt-1'>Not available</p>
                        )
                      ) : (
                        /* Short-answer: show AI score, not "Evaluated by AI" */
                        aiScorePct != null ? (
                          <div className='flex-1 flex flex-col items-center justify-center gap-1 rounded-lg px-2.5 py-3 bg-white border border-slate-200'>
                            <span className={`text-2xl font-bold tabular-nums leading-none ${scoreColor(aiScorePct)}`}>
                              { aiScorePct }%
                            </span>
                            <span className='flex items-center gap-1 text-[10px] text-slate-400 font-medium'>
                              <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.75}>
                                <path strokeLinecap='round' strokeLinejoin='round' d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
                              </svg>
                              AI evaluated
                            </span>
                          </div>
                        ) : (
                          <div className='flex-1 flex items-center justify-center rounded-lg px-2.5 py-3 bg-slate-50 border border-slate-100'>
                            <span className='text-xs text-slate-400 italic'>Pending evaluation</span>
                          </div>
                        )
                      ) }
                    </div>

                    { /* ③ Ideal Answer */ }
                    <div className='flex flex-col gap-1.5'>
                      <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-400'>
                        Ideal Answer
                      </p>
                      { snap?.idealAnswer ? (
                        <div className='flex-1 flex gap-1.5 rounded-lg px-2.5 py-2.5 bg-indigo-50 border border-indigo-100'>
                          <svg
                              className='w-3.5 h-3.5 text-indigo-400 flex-shrink-0 mt-0.5'
                              viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.75}
                          >
                            <path strokeLinecap='round' strokeLinejoin='round' d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
                          </svg>
                          <p className='text-xs text-indigo-700 leading-relaxed'>{ snap.idealAnswer }</p>
                        </div>
                      ) : (
                        <div className='flex-1 text-xs rounded-lg px-2.5 py-2.5 bg-slate-50 border border-slate-100 text-slate-400 italic'>
                          Not specified
                        </div>
                      ) }
                    </div>
                  </div>

                  { /* Metadata pills */ }
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <span className='text-[10px] bg-white border border-slate-200 text-slate-500 rounded-full px-2 py-0.5'>
                      { toDisplayName(qr.subdomain) }
                    </span>
                    { level != null && (
                      <span className='text-[10px] bg-white border border-slate-200 text-slate-500 rounded-full px-2 py-0.5'>
                        Level { level }
                      </span>
                    ) }
                    <span className='text-[10px] bg-white border border-slate-200 text-slate-500 rounded-full px-2 py-0.5 capitalize'>
                      { (snap?.questionType ?? qr.questionType).replace(/_/g, ' ') }
                    </span>
                    <span className='text-[10px] bg-white border border-slate-200 text-slate-500 rounded-full px-2 py-0.5'>
                      { formatDuration(qr.timeTakenSeconds) }
                    </span>
                  </div>
                </div>
              ) }
            </div>
          );
        }) }
      </div>
    </div>
  );
};

const ResultDetail: React.FC<{
  result: CCAApplicantResult;
  isLoading: boolean;
  isError: boolean;
}> = ({ result, isLoading, isError }) => {
  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8 gap-2'>
        <svg
            className='w-4 h-4 animate-spin text-emerald-500'
            viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}
        >
          <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
          />
        </svg>
        <span className='text-xs text-slate-500'>Loading detail…</span>
      </div>
    );
  }
  if (isError) {
    return (
      <p className='py-4 text-xs text-rose-500 text-center'>Failed to load result detail.</p>
    );
  }
  const assessment = result.assessment;
  const rawServerScores = assessment.subdomainScores ?? {};
  // API returns objects { score, maxScore, pct } — extract pct as number
  const extractedServerScores: Record<string, number> = {};
  for (const [key, val] of Object.entries(rawServerScores)) {
    if (typeof val === 'object' && val !== null && 'pct' in val) {
      extractedServerScores[key] = (val as { pct: number }).pct;
    } else if (typeof val === 'number') {
      extractedServerScores[key] = val;
    }
  }
  const subdomainScores =
    Object.keys(extractedServerScores).length > 0
      ? extractedServerScores
      : computeSubdomainScores(result.responses);

  const pct = assessment.totalPct != null ? Math.round(assessment.totalPct) : null;
  const hasRawAndMax = assessment.totalScore != null && assessment.maxScore != null;
  const hasAnyScore = pct != null || hasRawAndMax || assessment.totalScore != null;

  return (
    <div className='px-4 pb-4 pt-3 space-y-5 border-t border-slate-100 bg-slate-50/40'>
      { hasAnyScore && (
        <div className='flex items-baseline gap-2'>
          { pct != null ? (
            <>
              <span className={`text-2xl font-bold tabular-nums ${scoreColor(pct)}`}>
                { pct }%
              </span>
              { hasRawAndMax && (
                <span className='text-xs text-slate-400 font-medium'>
                  ({ assessment.totalScore }/{ assessment.maxScore } pts)
                </span>
              ) }
            </>
          ) : hasRawAndMax ? (
            <span className='text-2xl font-bold tabular-nums text-slate-700'>
              { assessment.totalScore }
              <span className='text-sm font-normal text-slate-400'>/{ assessment.maxScore } pts</span>
            </span>
          ) : (
            <span className='text-2xl font-bold tabular-nums text-slate-700'>
              { assessment.totalScore }
              <span className='text-sm font-normal text-slate-400 ml-1'>pts</span>
            </span>
          ) }
        </div>
      ) }
      <SubdomainBreakdown subdomainScores={subdomainScores} />
      <QuestionAccordion questionResults={result.responses} />
    </div>
  );
};

// ─── Results row ──────────────────────────────────────────────────────────────

interface ResultRowProps {
  summary: EnrichedResultSummary;
  isExpanded: boolean;
  onToggle: () => void;
  applicantResult?: CCAApplicantResult;
  isDetailLoading: boolean;
  isDetailError: boolean;
}

const ResultRow: React.FC<ResultRowProps> = ({
  summary,
  isExpanded,
  onToggle,
  applicantResult,
  isDetailLoading,
  isDetailError,
}) => {
  const scoreDisplay = getScoreDisplay(summary);
  return (
    <div className='border-b border-slate-100 last:border-b-0'>
      <button
          type='button'
          className='w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors'
          onClick={onToggle}
      >
        { /* Avatar + Name + email */ }
        <div className='flex-1 min-w-0'>
          <NameEmailAvatar
              name={`${summary.firstName} ${summary.lastName}`.trim() || summary.email || '—'}
              email={summary.email}
              avatar={undefined}
              avatarWidth='36px'
              avatarHeight='36px'
              nameFontSize='14px'
              emailFontSize='11px'
          />
        </div>

        { /* Status badge */ }
        <div className='flex-shrink-0 w-24 hidden sm:block'>
          <StatusBadge status={summary.status} />
        </div>

        { /* Score */ }
        <div className='flex-shrink-0 w-20'>
          { scoreDisplay != null ? (
            <span className={`text-sm font-bold ${scoreDisplay.pct != null ? scoreColor(scoreDisplay.pct) : 'text-slate-700'}`}>
              { scoreDisplay.text }
            </span>
          ) : summary.status === 'Completed' && summary.scoredAt == null ? (
            <span className='text-[11px] font-medium text-amber-500'>Pending</span>
          ) : (
            <span className='text-xs text-slate-400'>—</span>
          ) }
        </div>

        { /* Attempt */ }
        <div className='flex-shrink-0 w-14 hidden sm:block'>
          { summary.attemptNumber != null ? (
            <span className='inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600'>
              #{ summary.attemptNumber }
            </span>
          ) : (
            <span className='text-xs text-slate-400'>—</span>
          ) }
        </div>

        { /* Active time */ }
        <div className='flex-shrink-0 w-20 hidden lg:block'>
          <span className={`text-xs font-medium ${activeTimeColor(summary.activeTimeSeconds)}`}>
            { formatDuration(summary.activeTimeSeconds) }
          </span>
        </div>

        { /* Completed date */ }
        <div className='flex-shrink-0 w-28 text-xs text-slate-500 hidden md:block'>
          { formatDate(summary.completedAt) }
        </div>

        { /* Chevron */ }
        <svg
            className={`flex-shrink-0 w-4 h-4 text-slate-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
        >
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      { isExpanded && (
        applicantResult ? (
          <ResultDetail
              result={applicantResult}
              isLoading={isDetailLoading}
              isError={isDetailError}
          />
        ) : (
          <ResultDetail
              result={{
                assessment: {
                  applicantId: summary.applicantId,
                  jobOpeningId: '',
                  status: summary.status,
                  totalScore: null,
                  maxScore: null,
                  totalPct: null,
                  completedAt: null,
                  subdomainScores: {},
                },
                responses: [],
              }}
              isLoading={isDetailLoading}
              isError={isDetailError}
          />
        )
      ) }
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonRows: React.FC = () => (
  <>
    { Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className='flex items-center gap-3 px-4 py-3 border-b border-slate-100'>
        <div className='flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 animate-pulse' />
        <div className='flex-1 space-y-1.5'>
          <div className='h-3.5 w-36 rounded bg-slate-100 animate-pulse' />
          <div className='h-3 w-48 rounded bg-slate-100 animate-pulse' />
        </div>
        <div className='h-6 w-24 rounded-full bg-slate-100 animate-pulse hidden sm:block' />
        <div className='h-5 w-12 rounded bg-slate-100 animate-pulse' />
        <div className='h-5 w-10 rounded-full bg-slate-100 animate-pulse hidden sm:block' />
        <div className='h-4 w-14 rounded bg-slate-100 animate-pulse hidden lg:block' />
        <div className='h-3 w-20 rounded bg-slate-100 animate-pulse hidden md:block' />
        <div className='h-4 w-4 rounded bg-slate-100 animate-pulse' />
      </div>
    )) }
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface CCAResultsSectionProps {
  jobOpeningId: string;
}

export const CCAResultsSection: React.FC<CCAResultsSectionProps> = ({
  jobOpeningId,
}) => {
  const {
    results,
    total,
    isListLoading,
    isListError,
    selectedApplicantId,
    selectApplicant,
    applicantResult,
    isDetailLoading,
    isDetailError,
  } = useCCAResults(jobOpeningId);

  if (isListError) {
    return (
      <div className='flex flex-col items-center justify-center py-16'>
        <p className='text-sm font-medium text-rose-500'>
          Failed to load results.
        </p>
        <p className='text-xs text-slate-400 mt-1'>Please refresh the page.</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      { /* ── Header ───────────────────────────────────────────── */ }
      { !isListLoading && total > 0 && (
        <p className='text-sm font-semibold text-slate-700'>
          { total } result{ total !== 1 ? 's' : '' }
        </p>
      ) }

      { /* ── Results list ──────────────────────────────────────── */ }
      <div className='rounded-xl border border-slate-200 overflow-hidden bg-white'>
        { /* Table header */ }
        <div className='flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50'>
          <p className='flex-1 text-xs font-semibold uppercase tracking-wide text-slate-500 pl-12'>
            Candidate
          </p>
          <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 hidden sm:block w-24'>
            Status
          </p>
          <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 w-20'>
            Score
          </p>
          <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 w-14 hidden sm:block'>
            Attempt
          </p>
          <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 w-20 hidden lg:block'>
            Active Time
          </p>
          <p className='text-xs font-semibold uppercase tracking-wide text-slate-500 w-28 hidden md:block'>
            Completed
          </p>
          <div className='w-4' />
        </div>

        { isListLoading ? (
          <SkeletonRows />
        ) : results.length === 0 ? (
          <div className='px-4 py-14 text-center'>
            <p className='text-sm font-medium text-slate-500'>No results yet</p>
            <p className='text-xs text-slate-400 mt-1'>
              Switch to the Candidates tab to invite candidates to the assessment.
            </p>
          </div>
        ) : (
          results.map((summary) => (
            <ResultRow
                key={summary.applicantId}
                summary={summary}
                isExpanded={selectedApplicantId === summary.applicantId}
                onToggle={() => selectApplicant(summary.applicantId)}
                applicantResult={
                  selectedApplicantId === summary.applicantId
                    ? applicantResult
                    : undefined
                }
                isDetailLoading={
                  selectedApplicantId === summary.applicantId && isDetailLoading
                }
                isDetailError={
                  selectedApplicantId === summary.applicantId && isDetailError
                }
            />
          ))
        ) }
      </div>
    </div>
  );
};
